"use client";
import {
  GlobalNavPropsModel,
  NavItem,
} from "@/components/GlobalNav/GlobalNav.model";
import { cn } from "@/lib/utils";
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import { RxCross2 } from "react-icons/rx";
import "./GlobalNav.style.css";
import GlobalNavClosed from "./GlobalNavClosed/GlobalNavClosed.component";

const GlobalNav = ({
  className,
  items,
  title,
  logo,
  icon,
  handleContactClick,
}: GlobalNavPropsModel) => {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const menuItemsRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [open, setOpen] = useState(false);
  const [color, setColor] = useState("text-core-white");
  const [isOverFlow, setIsOverflow] = useState<boolean>(false);

  const toggleMenu = () => {
    if (open) {
      gsap.fromTo(
        containerRef?.current,
        {
          y: "0%",
        },
        {
          y: "100%",
          duration: 0.2,
          ease: "power2.inout",
        },
      );

      gsap.from(menuItemsRef?.current, {
        y: "0%",
        duration: 0.2,
        ease: "power2.inOut",
        onComplete: () => {
          const element: HTMLElement | null =
            document.getElementById("smooth-content");
          if (element) {
            element.style.visibility = "visible";
          }
          setOpen(false);
        },
      });
    } else {
      const element = document.getElementById("smooth-content");
      if (element) {
        element.style.visibility = "hidden";
      }
      setOpen(true);
    }
  };

  const onContactClick = () => {
    toggleMenu();
    setTimeout(() => {
      handleContactClick();
    }, 500);
  };

  useGSAP(() => {
    const scrollItems = (e: any) => {
      const menuElement = menuRef?.current;
      const menuItemsElement = menuItemsRef?.current;
      const wrapHeight = menuElement?.clientHeight;
      const listHeight = menuItemsElement?.scrollHeight;
      if (!menuElement || !menuItemsElement) return;
      if (wrapHeight && wrapHeight < 0) return;
      if (wrapHeight && listHeight) {
        const dP = e.clientY / wrapHeight;
        gsap.to(menuItemsElement, {
          duration: 0.5,
          y: dP * (listHeight - wrapHeight + 100) * -1,
          ease: "power3",
        });
      }
    };

    document.addEventListener("mousemove", scrollItems);
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keydown", event => {
      if (event.key === "Tab") scrollItems(event);
    });
    return () => {
      document.removeEventListener("mousemove", scrollItems);
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keydown", event => {
        if (event.key === "Tab") scrollItems(event);
      });
    };
  }, []);

  useGSAP(() => {
    if (open)
      gsap.fromTo(
        containerRef?.current,
        { y: "100%" },
        { y: "0%", duration: 0.2, ease: "power5.inOut" },
      );
  }, [open]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex(prevIndex => {
          const newIndex = prevIndex === items.length - 1 ? 0 : prevIndex + 1;

          return newIndex;
        });
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex(prevIndex => {
          const newIndex = prevIndex === 0 ? items.length - 1 : prevIndex - 1;

          return newIndex;
        });
      }
    },
    [items],
  );

  useEffect(() => {
    const focusItem = (index: number) => {
      const menuItems = menuItemsRef.current?.children;
      if (menuItems && menuItems[index]) {
        (menuItems[index] as HTMLElement).focus();
      }
    };

    focusItem(selectedIndex);
  }, [selectedIndex]);

  useEffect(() => {
    if (open && menuRef?.current) {
      setIsOverflow(
        menuRef.current.scrollHeight > menuRef.current.clientHeight,
      );
    }
  }, [open]);

  const cssClasses = open
    ? "bg-core-red"
    : "bg-core-black/80 hover:bg-core-black active:bg-core-black focus:bg-core-black";

  return (
    <div className={cn(className, "GlobalNav")}>
      <GlobalNavClosed
        className={cn("GlobalNav__Closed z-50", cssClasses, {
          "GlobalNav__Closed--narrow-width": open,
        })}
        title={open ? "CLOSE" : title}
        logo={open ? "" : logo}
        icon={open ? <RxCross2 /> : icon}
        toggleMenu={toggleMenu}
        horizontalPosition={open ? 100 : -100}
        hideOnSrcollDown={open ? false : true}
      />

      {open && (
        <div
          ref={containerRef}
          className="menu no-scrollbar bg-core-black/80 fixed top-0 right-0 z-40 h-screen w-full overflow-auto backdrop-blur-xl"
        >
          <div
            ref={menuRef}
            className={`font-JetBrainsRegular flex h-screen font-normal ${color}`}
          >
            <div
              ref={menuItemsRef}
              className={cn(
                isOverFlow ? "mt-[100px]" : "justify-center",
                `min-lg:hover:text-core-white20 min-lg:focus:text-core-white20 min-lg:active:text-core-white20 flex h-screen w-full flex-col text-center`,
              )}
            >
              {items?.map((item: NavItem, index: number) => (
                <Link
                  style={{ transition: "all .5s ease-in-out" }}
                  key={index}
                  href={item?.slug === "/" ? item.slug : "/" + item?.slug}
                  className={cn(
                    `${selectedIndex === index && "active text-core-white"}`,
                    `text-h2 hover:text-core-red focus:text-core-red active:text-core-red max-lg:text-display min-lg:text-display relative no-underline outline-none`,
                  )}
                  onFocus={() => setColor("text-core-white")}
                  onClick={toggleMenu}
                  tabIndex={0}
                  onKeyDown={event => {
                    if (event.key === "Enter" && toggleMenu) {
                      toggleMenu();
                    }
                  }}
                >
                  <span>{item?.title?.toUpperCase()}</span>
                </Link>
              ))}
              <Link
                style={{ transition: "all .5s ease-in-out" }}
                key="contact"
                href="#"
                className={cn(
                  `${selectedIndex === items.length && "active text-core-white"}`,
                  `text-h2 hover:text-core-red focus:text-core-red active:text-core-red max-lg:text-display min-lg:text-display relative no-underline outline-none`,
                )}
                onFocus={() => setColor("text-core-white")}
                onClick={onContactClick}
                tabIndex={0}
                onKeyDown={event => {
                  if (event.key === "Enter" && onContactClick) {
                    onContactClick();
                  }
                }}
              >
                <span>Contact</span>
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalNav;
